import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Clapperboard, Loader2, RefreshCw, Trash2, Video, Camera, Sun, Film, MapPin, Pencil, Check, X, ChevronUp, ChevronDown, Plus, Radio, Map as MapIcon, PersonStanding, HeartHandshake, Check as CheckIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from '@/components/ui/sonner';
import {
  adminSimulateScout, adminDeleteSimulation, adminAddScoutMarker, adminUpdateScoutMarker, adminDeleteScoutMarker, adminScoutSunNow, adminRefreshScoutSite,
  adminEditSimFrame, adminAddSimFrame, adminDeleteSimFrame, adminReorderSimFrames, adminUpdateScout,
} from '@/lib/api';
import { BlockingDiagram, BlockingLegend, SiteKey, MARKER_STYLE } from '@/components/scout/BlockingDiagram';
import { PinToolbar } from '@/components/scout/PinToolbar';
import { PosesTab } from '@/components/scout/PosesTab';
import { SIMULATOR, POSES } from '@/constants/testIds';
import { SkeletonList } from '@/components/SkeletonList';
import { readBoolPref, writeBoolPref } from '@/lib/prefs';

const STATUS_LABEL = { generating: 'Plotting positions…', rendering: 'Finishing…', ready: 'Positions ready', failed: 'Simulation failed' };
const LIVE_KEY = 'lxra.scout.liveSun';
const fieldClass = 'h-8 bg-[#141416] border-[hsl(var(--lxra-border))] text-[hsl(var(--lxra-fg))] text-xs focus-visible:ring-[hsl(var(--lxra-gold))]';

function SunChip({ sun }) {
  if (!sun) return null;
  const below = sun.elevation < -0.8;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] whitespace-nowrap ${below ? 'border-sky-400/30 text-sky-300' : 'border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]'}`}>
      <Sun className="h-3 w-3" /> {below ? 'below horizon' : `${sun.direction} · ${sun.elevation}°`} · {sun.phase}
    </span>
  );
}

function KV({ k, v }) {
  if (!v && v !== 0) return null;
  return (
    <div className="flex justify-between gap-3 border-b border-[hsl(var(--lxra-border))]/60 py-1.5 text-xs">
      <span className="text-[hsl(var(--lxra-muted))] shrink-0">{k}</span>
      <span className="text-[hsl(var(--lxra-fg))] text-right">{v}</span>
    </div>
  );
}

/** Live "now" strip: current sun + next light window, refreshed every minute. */
function useLiveSun(reportId, enabled) {
  const [live, setLive] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!enabled || !reportId) { setLive(null); return undefined; }
    let cancelled = false;
    const tick = () => adminScoutSunNow(reportId).then((d) => { if (!cancelled) { setLive(d); setError(null); } }).catch(() => { if (!cancelled) setError('Live sun unavailable'); });
    tick();
    const id = setInterval(tick, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [reportId, enabled]);
  return { live, error };
}

function LiveSunStrip({ live, error }) {
  if (error) return <p data-testid={SIMULATOR.liveSunStrip} className="text-[11px] text-amber-300">{error}</p>;
  if (!live) return <div data-testid={SIMULATOR.liveSunStrip} aria-busy="true"><Skeleton className="h-12 rounded-xl bg-[#141416]" /></div>;
  const s = live.sun;
  const below = s.elevation < -0.8;
  return (
    <div data-testid={SIMULATOR.liveSunStrip} className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-[#FFF5D6]/20 bg-[#FFF5D6]/[0.04] px-4 py-3 text-xs">
      <span className="inline-flex items-center gap-2 text-[hsl(var(--lxra-fg))]">
        <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFF5D6] opacity-60" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#FFF5D6]" /></span>
        <strong className="font-display text-base leading-none">{live.clock}</strong>
        <span className="text-[hsl(var(--lxra-muted))]">{live.timezone.split('/').pop().replace('_', ' ')}{live.is_shoot_day ? ' · shoot day' : ''}</span>
      </span>
      <span className={below ? 'text-sky-300' : 'text-[hsl(var(--lxra-gold))]'}>
        Sun {below ? 'below the horizon' : `${s.direction} · ${s.elevation}° up`} · {s.phase}
      </span>
      {live.current_window && <span className="text-emerald-300">{live.current_window.label} now — {live.current_window.minutes_left} min left</span>}
      {!live.current_window && live.next_window && (
        <span className="text-[hsl(var(--lxra-muted))]">
          {live.next_window.label} at <span className="text-[hsl(var(--lxra-fg))]">{live.next_window.start_clock}</span> · in {live.next_window.minutes_until >= 60 ? `${Math.floor(live.next_window.minutes_until / 60)}h ${live.next_window.minutes_until % 60}m` : `${live.next_window.minutes_until} min`}
        </span>
      )}
      <span className="ml-auto text-[10px] text-[hsl(var(--lxra-muted))]">Sunrise {live.sunrise_clock} · Sunset {live.sunset_clock}</span>
    </div>
  );
}

function ComfortNotes({ report, onChanged }) {
  const [value, setValue] = useState(report.comfort_notes || '');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  useEffect(() => { setValue(report.comfort_notes || ''); }, [report.comfort_notes]);
  const dirty = value.trim() !== (report.comfort_notes || '').trim();

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await adminUpdateScout(report.id, { comfort_notes: value.trim() });
      setSavedAt(Date.now());
      toast.success('Comfort notes saved — new poses and re-plots will honour them.');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save notes.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Label htmlFor="comfort-notes" className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))] flex items-center gap-1.5"><HeartHandshake className="h-3.5 w-3.5 text-[hsl(var(--lxra-gold))]" /> Client comfort notes</Label>
          <Textarea
            id="comfort-notes"
            data-testid={POSES.comfortNotes}
            rows={2}
            maxLength={1000}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={save}
            placeholder="e.g. Bride is self-conscious about her arms · Groom is 6'4&quot; · Grandma can't kneel or sit on the ground"
            className={`${fieldClass} mt-1.5 h-auto resize-none`}
          />
          <p className="mt-1 text-[10px] text-[hsl(var(--lxra-muted))]">Pose suggestions honour these literally; re-plotting the simulation uses them too. Saves when you click away.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 pt-5">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[hsl(var(--lxra-gold))]" /> : savedAt && !dirty ? <span data-testid={POSES.comfortSaved} className="inline-flex items-center gap-1 text-[10px] text-emerald-300"><CheckIcon className="h-3 w-3" /> Saved</span> : null}
        </div>
      </div>
    </div>
  );
}

function FrameCard({ frame, count, reportId, site, sunTrack, liveSun, onChanged, pins, comfortNotes }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const p = frame.photo || {};
  const v = frame.video || {};

  const startEdit = () => { setForm({ title: frame.title || '', time: frame.time || '', spot: frame.spot || '', moment: frame.moment || '' }); setEditing(true); };

  const save = async () => {
    setBusy(true);
    try {
      await adminEditSimFrame(reportId, frame.idx, form);
      toast.success('Moment updated.');
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save.'); }
    finally { setBusy(false); }
  };

  const commitBlocking = useCallback(async (blocking) => {
    try {
      await adminEditSimFrame(reportId, frame.idx, { blocking: { subjects: blocking.subjects, camera: blocking.camera, camera_move_to: blocking.camera_move_to ?? null } });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not move.'); }
  }, [reportId, frame.idx, onChanged]);

  const move = async (dir) => {
    const order = Array.from({ length: count }, (_, i) => i);
    const j = frame.idx + dir;
    if (j < 0 || j >= count) return;
    [order[frame.idx], order[j]] = [order[j], order[frame.idx]];
    try { await adminReorderSimFrames(reportId, order); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not reorder.'); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${frame.title}"?`)) return;
    try { await adminDeleteSimFrame(reportId, frame.idx); toast.success('Moment removed.'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete.'); }
  };

  return (
    <article data-testid={SIMULATOR.frame(frame.idx)} className={`rounded-2xl border bg-[#0F0F10] overflow-hidden ${editing ? 'border-[hsl(var(--lxra-gold))]/60' : 'border-[hsl(var(--lxra-border))]'}`}>
      <div className="p-4 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))] flex items-center gap-2">
              Moment {frame.idx + 1} · {frame.time}
              {frame.edited && <span data-testid={SIMULATOR.editedBadge(frame.idx)} className="rounded-full border border-[hsl(var(--lxra-border))] px-1.5 py-px text-[9px] normal-case tracking-normal text-[hsl(var(--lxra-muted))]">{frame.custom ? 'custom' : 'edited'}</span>}
            </p>
            {editing ? (
              <div className="mt-2 space-y-2">
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Moment name" data-testid={SIMULATOR.editTitle(frame.idx)} className={fieldClass} />
                <div className="grid grid-cols-[110px_1fr] gap-2">
                  <Input value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} placeholder="6:20 PM" data-testid={SIMULATOR.editTime(frame.idx)} className={fieldClass} />
                  <Input value={form.spot} onChange={(e) => setForm({ ...form, spot: e.target.value })} placeholder="Spot within the location" data-testid={SIMULATOR.editSpot(frame.idx)} className={fieldClass} />
                </div>
                <Textarea rows={2} value={form.moment} onChange={(e) => setForm({ ...form, moment: e.target.value })} placeholder="What's happening in this moment" data-testid={SIMULATOR.editMoment(frame.idx)} className={`${fieldClass} h-auto resize-none`} />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={save} disabled={busy} data-testid={SIMULATOR.editSave(frame.idx)} className="h-7 px-3 bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-[11px] uppercase tracking-[0.08em]">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Save</>}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid={SIMULATOR.editCancel(frame.idx)} className="h-7 px-2 text-[11px] text-[hsl(var(--lxra-muted))]"><X className="h-3 w-3 mr-1" /> Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <h4 className="font-display text-lg leading-tight text-[hsl(var(--lxra-fg))] mt-1">{frame.title}</h4>
                {frame.spot && <p className="text-[11px] text-[hsl(var(--lxra-muted))] mt-0.5 flex items-center gap-1"><MapPin className="h-3 w-3" /> {frame.spot}</p>}
              </>
            )}
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <SunChip sun={frame.sun} />
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={() => move(-1)} disabled={frame.idx === 0} data-testid={SIMULATOR.moveUp(frame.idx)} title="Move earlier" className="h-6 w-6 inline-flex items-center justify-center rounded text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] disabled:opacity-30"><ChevronUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(1)} disabled={frame.idx === count - 1} data-testid={SIMULATOR.moveDown(frame.idx)} title="Move later" className="h-6 w-6 inline-flex items-center justify-center rounded text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] disabled:opacity-30"><ChevronDown className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={editing ? () => setEditing(false) : startEdit} data-testid={SIMULATOR.editButton(frame.idx)} title="Rename / retime" className={`h-6 w-6 inline-flex items-center justify-center rounded hover:text-[hsl(var(--lxra-gold))] ${editing ? 'text-[hsl(var(--lxra-gold))]' : 'text-[hsl(var(--lxra-muted))]'}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={remove} disabled={count <= 1} data-testid={SIMULATOR.deleteFrame(frame.idx)} title="Delete moment" className="h-6 w-6 inline-flex items-center justify-center rounded text-[hsl(var(--lxra-muted))] hover:text-red-400 disabled:opacity-30"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        </div>
      </div>
      <div className="px-4">
        <div className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0B0B0C] p-2">
          <BlockingDiagram blocking={frame.blocking} fovDeg={p.fov_deg || 40} testId={SIMULATOR.blocking(frame.idx)} sunTrack={sunTrack} site={site} liveSun={liveSun} editable={!pins.placing} onCommit={commitBlocking} {...pins.diagramProps} />
          <BlockingLegend blocking={frame.blocking} fovDeg={p.fov_deg || 40} site={site} />
        </div>
        {frame.blocking?.notes && <p className="mt-2 text-xs text-[hsl(var(--lxra-muted))]">{frame.blocking.notes}</p>}
      </div>
      {frame.moment && !editing && <p className="px-4 pt-3 text-sm text-[hsl(var(--lxra-fg))]/90 leading-relaxed">{frame.moment}</p>}

      <Tabs defaultValue="photo" className="px-4 pb-4 pt-3">
        <TabsList className="grid w-full grid-cols-3 bg-[#141416] h-9">
          <TabsTrigger value="photo" data-testid={SIMULATOR.frameTab(frame.idx, 'photo')} className="text-[11px] data-[state=active]:bg-[hsl(var(--lxra-gold))] data-[state=active]:text-[#0A0A0A]"><Camera className="h-3.5 w-3.5 mr-1" /> Photo</TabsTrigger>
          <TabsTrigger value="video" data-testid={SIMULATOR.frameTab(frame.idx, 'video')} className="text-[11px] data-[state=active]:bg-[hsl(var(--lxra-gold))] data-[state=active]:text-[#0A0A0A]"><Video className="h-3.5 w-3.5 mr-1" /> Video</TabsTrigger>
          <TabsTrigger value="poses" data-testid={POSES.tab(frame.idx)} className="text-[11px] data-[state=active]:bg-[hsl(var(--lxra-gold))] data-[state=active]:text-[#0A0A0A]"><PersonStanding className="h-3.5 w-3.5 mr-1" /> Poses{frame.poses?.items?.length ? <span className="ml-1 rounded-full bg-[#0A0A0A]/20 px-1 text-[9px] font-mono">{frame.poses.items.length}</span> : null}</TabsTrigger>
        </TabsList>
        <TabsContent value="photo" className="mt-3">
          <KV k="Lens" v={p.lens} />
          <KV k="Framing" v={[p.framing, p.angle, p.orientation].filter(Boolean).join(' · ')} />
          <KV k="Exposure" v={[p.aperture, p.shutter, p.iso ? `ISO ${p.iso}` : null].filter(Boolean).join(' · ')} />
          <KV k="White balance" v={p.white_balance} />
          <KV k="Light" v={frame.light_intent} />
          {p.direction_cue && <p className="mt-2 text-xs italic text-[hsl(var(--lxra-muted))]">Say: &ldquo;{p.direction_cue}&rdquo;</p>}
          {!p.lens && <p className="text-xs text-[hsl(var(--lxra-muted))]">Custom moment — copy settings from a neighbour or shoot it your way.</p>}
        </TabsContent>
        <TabsContent value="video" className="mt-3">
          <KV k="Shot" v={[v.shot_type, v.movement].filter(Boolean).join(' · ')} />
          <KV k="Duration" v={v.duration_s ? `${v.duration_s}s` : null} />
          <KV k="Frame rate" v={v.fps ? `${v.fps} fps · ${v.shutter || ''}` : null} />
          <KV k="Lens" v={v.lens} />
          <KV k="Rig" v={v.stabilization} />
          {v.notes && <p className="mt-2 text-xs text-[hsl(var(--lxra-muted))]">{v.notes}</p>}
        </TabsContent>
        <TabsContent value="poses" className="mt-3">
          <PosesTab frame={frame} reportId={reportId} comfortNotes={comfortNotes} onChanged={onChanged} />
        </TabsContent>
      </Tabs>
    </article>
  );
}

function AddMomentCard({ reportId, count, onChanged }) {
  const [title, setTitle] = useState('');
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await adminAddSimFrame(reportId, { title, time });
      toast.success('Moment added — drag the dots to place everyone.');
      setTitle(''); setTime('');
      onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not add the moment.'); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={add} data-testid={SIMULATOR.addCard} className="rounded-2xl border border-dashed border-[hsl(var(--lxra-gold))]/40 bg-[hsl(var(--lxra-gold))]/[0.03] p-5 flex flex-col justify-center gap-3 min-h-[220px]">
      <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-gold))] flex items-center gap-2"><Plus className="h-3.5 w-3.5" /> Add your own moment</p>
      <p className="text-xs text-[hsl(var(--lxra-muted))]">Positions start from the previous moment so you only need to nudge them.</p>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Cap toss on the steps" data-testid={SIMULATOR.addTitle} className={fieldClass} />
      <Input value={time} onChange={(e) => setTime(e.target.value)} placeholder="Time, e.g. 6:45 PM" required data-testid={SIMULATOR.addTime} className={fieldClass} />
      <Button type="submit" disabled={busy || !time.trim() || count >= 12} data-testid={SIMULATOR.addSubmit} className="h-9 bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-[11px] uppercase tracking-[0.08em] disabled:opacity-60">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Add moment'}
      </Button>
      {count >= 12 && <p className="text-[10px] text-[hsl(var(--lxra-muted))]">A simulation holds up to 12 moments.</p>}
    </form>
  );
}

export const ShootSimulatorSection = ({ report, onChanged }) => {
  const sim = report.simulation;
  const site = report.site || null;
  const sunTrack = report.sun_track || [];
  const [frameCount, setFrameCount] = useState(sim?.frame_count || 5);
  const [progression, setProgression] = useState(sim?.timelapse_enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [liveOn, setLiveOn] = useState(() => readBoolPref(LIVE_KEY, false));
  const [placing, setPlacing] = useState(null); // marker kind being placed, or null
  const [pinLabel, setPinLabel] = useState('');
  const [selectedPin, setSelectedPin] = useState(null);
  const markers = report.markers || [];

  const placePin = async ({ x, y }) => {
    if (!placing) return;
    try {
      await adminAddScoutMarker(report.id, { x, y, kind: placing, label: pinLabel.trim() });
      toast.success(`${MARKER_STYLE[placing]?.label || 'Pin'} added.`);
      setPlacing(null);
      setPinLabel('');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add the pin.'); }
  };
  const movePin = async (id, { x, y }) => {
    try { await adminUpdateScoutMarker(report.id, id, { x, y }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not move the pin.'); }
  };
  const removePin = async (id) => {
    try { await adminDeleteScoutMarker(report.id, id); setSelectedPin(null); toast.success('Pin removed.'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not remove the pin.'); }
  };
  const renamePin = async (id, label) => {
    try { await adminUpdateScoutMarker(report.id, id, { label }); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not rename the pin.'); }
  };
  const pins = {
    placing,
    diagramProps: { markers, placing, onPlace: placePin, onMarkerMove: movePin, onMarkerSelect: setSelectedPin, selectedMarkerId: selectedPin },
  };
  const [siteBusy, setSiteBusy] = useState(false);
  const { live, error: liveError } = useLiveSun(report.id, liveOn);
  const liveSun = liveOn && live ? live.sun : null;

  const toggleLive = (v) => { setLiveOn(v); writeBoolPref(LIVE_KEY, v); };

  const running = sim && (sim.status === 'generating' || sim.status === 'rendering');
  const legacyRenders = useMemo(() => (sim ? [...(sim.frames || []), ...(sim.timelapse || [])].some((f) => f.image_path) : false), [sim]);

  const run = async () => {
    setBusy(true);
    try {
      await adminSimulateScout(report.id, { frame_count: frameCount, progression, render_images: false });
      toast.success('Plotting the shoot — positions will appear in a few seconds.');
      setShowSetup(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start the simulation.'); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm('Remove this simulation from the report?')) return;
    setBusy(true);
    try { await adminDeleteSimulation(report.id); toast.success('Simulation cleared.'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear.'); }
    finally { setBusy(false); }
  };

  const refreshSite = async () => {
    setSiteBusy(true);
    try { const s = await adminRefreshScoutSite(report.id); toast.success(`Site map updated — ${s.features?.length || 0} features.`); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not refresh the site map.'); }
    finally { setSiteBusy(false); }
  };

  const setup = (
    <div data-testid={SIMULATOR.setup} className="rounded-2xl border border-[hsl(var(--lxra-gold))]/30 bg-[hsl(var(--lxra-gold))]/[0.04] p-5 sm:p-6">
      <div className="grid gap-6 sm:grid-cols-[1fr_auto] sm:items-end">
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.1em] text-[hsl(var(--lxra-muted))]">Moments to plot</Label>
              <span data-testid={SIMULATOR.frameCount} className="font-display text-2xl leading-none text-[hsl(var(--lxra-fg))]">{frameCount}</span>
            </div>
            <Slider data-testid={SIMULATOR.frameSlider} value={[frameCount]} min={3} max={8} step={1} onValueChange={([v]) => setFrameCount(v)} className="mt-3 [&_[role=slider]]:bg-[hsl(var(--lxra-gold))] [&_[role=slider]]:border-[hsl(var(--lxra-gold))] [&_.bg-primary]:bg-[hsl(var(--lxra-gold))]" />
            <p className="mt-2 text-[11px] text-[hsl(var(--lxra-muted))]">Each moment = one beat of the occasion at its exact sun position, placed on the real map of the site: where the subjects stand, where you stand, how you move, and the photo + video settings.</p>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-4 py-3">
            <div>
              <p className="text-sm text-[hsl(var(--lxra-fg))]">Light progression</p>
              <p className="text-[11px] text-[hsl(var(--lxra-muted))]">The hero spot at 3 times — positions rotate as the sun moves.</p>
            </div>
            <Switch checked={progression} onCheckedChange={setProgression} data-testid={SIMULATOR.timelapseSwitch} className="data-[state=checked]:bg-[hsl(var(--lxra-gold))]" />
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:w-56">
          <p data-testid={SIMULATOR.estimate} className="text-center text-[11px] text-[hsl(var(--lxra-muted))]">{frameCount + (progression ? 3 : 0)} diagrams · no image renders · ~20s</p>
          <Button onClick={run} disabled={busy || running} data-testid={SIMULATOR.runButton} className="h-11 rounded-xl bg-[hsl(var(--lxra-gold))] text-[#0A0A0A] hover:bg-[hsl(var(--lxra-gold-hover))] text-xs uppercase tracking-[0.1em] font-semibold disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Clapperboard className="h-4 w-4 mr-2" /> {sim ? 'Re-plot' : 'Simulate the shoot'}</>}
          </Button>
          {sim && !running && <Button variant="ghost" onClick={() => setShowSetup(false)} className="h-8 text-xs text-[hsl(var(--lxra-muted))]">Cancel</Button>}
        </div>
      </div>
    </div>
  );

  return (
    <section data-testid={SIMULATOR.section} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10]/60 p-5 sm:p-7">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--lxra-gold))] flex items-center gap-2"><Film className="h-3.5 w-3.5" /> Shoot Simulator</p>
          <h3 className="font-display text-2xl text-[hsl(var(--lxra-fg))] mt-1">Where everyone stands, moment by moment</h3>
          <p className="text-xs text-[hsl(var(--lxra-muted))] mt-1 max-w-xl">A top-down plan of {report.request?.occasion || 'the session'} at {report.place?.name} on the real map of the site: the sun&apos;s true position at each time, where your subjects stand, where you stand, how you move, and how you shoot it for photo and video.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--lxra-border))] px-3 py-1.5 text-[11px] text-[hsl(var(--lxra-fg))] cursor-pointer hover:border-[#FFF5D6]/40">
            <Radio className={`h-3.5 w-3.5 ${liveOn ? 'text-[#FFF5D6]' : 'text-[hsl(var(--lxra-muted))]'}`} />
            Live sun
            <Switch checked={liveOn} onCheckedChange={toggleLive} data-testid={SIMULATOR.liveSunSwitch} className="h-4 w-7 [&>span]:h-3 [&>span]:w-3 [&>span]:data-[state=checked]:translate-x-3 data-[state=checked]:bg-[#FFF5D6]" />
          </label>
          {sim && (
            <>
              <Badge data-testid={SIMULATOR.status} variant="outline" className={`gap-1.5 text-[10px] uppercase tracking-[0.08em] ${sim.status === 'ready' ? 'border-emerald-400/40 text-emerald-300' : sim.status === 'failed' ? 'border-red-400/40 text-red-300' : 'border-[hsl(var(--lxra-gold))]/40 text-[hsl(var(--lxra-gold))]'}`}>
                {running && <Loader2 className="h-3 w-3 animate-spin" />}{STATUS_LABEL[sim.status] || sim.status}
              </Badge>
              {!running && (
                <>
                  <Button size="sm" variant="outline" onClick={() => setShowSetup((v) => !v)} data-testid={SIMULATOR.rerunButton} className="h-8 border-[hsl(var(--lxra-border))] text-xs text-[hsl(var(--lxra-fg))] hover:border-[hsl(var(--lxra-gold))] hover:text-[hsl(var(--lxra-gold))]"><RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Re-plot</Button>
                  <Button size="icon" variant="ghost" onClick={clear} disabled={busy} data-testid={SIMULATOR.clearButton} title="Clear simulation" className="h-8 w-8 text-[hsl(var(--lxra-muted))] hover:text-red-400 hover:bg-red-400/10"><Trash2 className="h-4 w-4" /></Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-6 space-y-6">
        {liveOn && <LiveSunStrip live={live} error={liveError} />}

        {(!sim || showSetup) && setup}

        {sim && running && (
          <div data-testid={SIMULATOR.progress} className="rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-4">
            <div className="flex items-center justify-between text-xs">
              <span className="text-[hsl(var(--lxra-fg))] inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-[hsl(var(--lxra-gold))]" /> {STATUS_LABEL[sim.status]}</span>
              <span className="text-[hsl(var(--lxra-muted))]">Planning {sim.frame_count} moments{sim.timelapse_enabled ? ' + light progression' : ''}…</span>
            </div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#1C1C1F]"><div className="h-full w-1/3 rounded-full bg-[hsl(var(--lxra-gold))] animate-pulse" /></div>
          </div>
        )}

        {sim?.status === 'failed' && (
          <div data-testid={SIMULATOR.error} className="rounded-xl border border-red-400/30 bg-red-400/5 p-4 text-sm text-red-200">{sim.error || 'The simulation failed.'} <button type="button" onClick={() => setShowSetup(true)} className="underline underline-offset-2 hover:text-[hsl(var(--lxra-gold))]">Try again</button></div>
        )}

        {legacyRenders && sim?.status === 'ready' && (
          <p className="text-[11px] text-[hsl(var(--lxra-muted))]">This simulation was made in the older render mode. Re-plot to get the positions-only view with the light progression.</p>
        )}

        {sim?.status === 'ready' && <ComfortNotes report={report} onChanged={onChanged} />}

        {sim?.status === 'ready' && (
          <PinToolbar
            markers={markers}
            placing={placing}
            onPlacingChange={(k) => { setPlacing(k); setSelectedPin(null); }}
            label={pinLabel}
            onLabelChange={setPinLabel}
            selectedId={selectedPin}
            onSelect={setSelectedPin}
            onRemove={removePin}
            onRename={renamePin}
          />
        )}

        {sim?.status === 'ready' && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] px-4 py-2.5">
            {site ? <SiteKey site={site} /> : <span data-testid={SIMULATOR.siteError} className="text-[11px] text-amber-300 inline-flex items-center gap-1.5"><MapIcon className="h-3.5 w-3.5" /> No site map yet — refresh to load real obstacles.</span>}
            <div className="flex items-center gap-3">
              {site?.error && <span data-testid={SIMULATOR.siteError} className="text-[10px] text-amber-300">{site.error}</span>}
              <button type="button" onClick={refreshSite} disabled={siteBusy} data-testid={SIMULATOR.siteRefresh} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-muted))] hover:text-[hsl(var(--lxra-gold))] disabled:opacity-50">
                {siteBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <MapIcon className="h-3 w-3" />} Refresh map
              </button>
            </div>
          </div>
        )}

        {sim?.timelapse?.length > 0 && sim.timelapse.some((t) => t.blocking) && (
          <div data-testid={SIMULATOR.timelapseStrip}>
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--lxra-muted))]">Light progression · <span data-testid={SIMULATOR.heroSpot} className="text-[hsl(var(--lxra-fg))] normal-case tracking-normal">{sim.hero_spot || sim.timelapse[0]?.spot}</span></p>
              <p className="text-[10px] text-[hsl(var(--lxra-muted))]">Same spot — watch the sun move and the positions turn with it</p>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {sim.timelapse.map((t) => (
                <div key={t.idx} data-testid={SIMULATOR.timelapseFrame(t.idx)} className="rounded-2xl border border-[hsl(var(--lxra-border))] bg-[#0F0F10] p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <span className="font-display text-xl leading-none text-[hsl(var(--lxra-fg))]">{t.time}</span>
                    <SunChip sun={t.sun} />
                  </div>
                  <div className="rounded-xl bg-[#0B0B0C] p-1.5">
                    <BlockingDiagram blocking={t.blocking} fovDeg={t.fov_deg || 40} sunTrack={sunTrack} site={site} liveSun={liveSun} compact {...pins.diagramProps} />
                    <BlockingLegend blocking={t.blocking} fovDeg={t.fov_deg || 40} site={site} />
                  </div>
                  {(t.light || t.blocking?.notes) && <p className="mt-2 text-[11px] text-[hsl(var(--lxra-muted))]">{t.light}{t.light && t.blocking?.notes ? ' — ' : ''}{t.blocking?.notes}</p>}
                  {t.lens && <p className="mt-1 text-[10px] uppercase tracking-[0.08em] text-[hsl(var(--lxra-gold))]">{t.lens}</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {sim?.frames?.length > 0 && (
          <>
            <p data-testid={SIMULATOR.dragHint} className="text-[11px] text-[hsl(var(--lxra-muted))]">Tip: drag the white subject dots, the gold camera, or the dashed move target on any moment to refine positions — the light math updates on release. Use the pencil to rename or retime.</p>
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {sim.frames.map((f) => <FrameCard key={`${f.idx}-${f.updated_at || ''}`} frame={f} count={sim.frames.length} reportId={report.id} site={site} sunTrack={sunTrack} liveSun={liveSun} onChanged={onChanged} pins={pins} comfortNotes={report.comfort_notes} />)}
              <AddMomentCard reportId={report.id} count={sim.frames.length} onChanged={onChanged} />
            </div>
          </>
        )}

        {sim && running && !sim.frames?.length && (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            <SkeletonList count={sim.frame_count || 3} className="h-80 rounded-2xl bg-[#141416]" />
          </div>
        )}
      </div>
    </section>
  );
};
